import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { PageHeader, Card, Badge, LoadingSpinner, ErrorMessage, colors } from '../components/SharedComponents';
import SalesAccountDirectory from '../components/SalesAccountDirectory';
import { API_BASE } from '../config';

function Accounts() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const customerCode = params.get('customer');
  if (customerCode && !id) return <Navigate replace to={`/customers?${params}`} />;
  return id ? <AccountDetail accountId={id} /> : <AccountsList />;
}

// ═══════ ACCOUNTS LIST VIEW ═══════

function AccountsList() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAccounts();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAccounts = async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/accounts`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (res.ok) {
        setAccounts(await res.json());
      } else {
        setError('Failed to load accounts');
      }
    } catch (err) {
      console.error('Error fetching accounts:', err);
      setError('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '$0';
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  if (loading) {
    return (
      <div style={{ padding: '32px', backgroundColor: colors.lightGray, minHeight: '100vh' }}>
        <LoadingSpinner text="Loading accounts..." />
        <SalesAccountDirectory key="sales-directory" token={token} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '32px 40px' }}>
        <PageHeader title="Accounts" subtitle="Customer accounts and demand context intelligence" />
        <Card><ErrorMessage message={error} onRetry={fetchAccounts} /></Card>
        <SalesAccountDirectory key="sales-directory" token={token} />
      </div>
    );
  }

  const totalRevenue = accounts.reduce((sum, a) => sum + (a.annual_value || 0), 0);
  const accountsWithIntel = accounts.filter(a => a.intelligence_count > 0).length;

  return (
    <div style={{ padding: '32px', backgroundColor: colors.lightGray, minHeight: '100vh' }}>
      <PageHeader
        title="Accounts"
        subtitle="Customer accounts and demand context intelligence"
      />

      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '24px' }}>
        <Card padding="16px 20px">
          <div style={{ fontSize: 'var(--font-size-hero)', fontWeight: 'var(--font-weight-bold)', color: colors.darkText, marginBottom: '4px' }}>{accounts.length}</div>
          <div style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>total accounts</div>
        </Card>
        <Card padding="16px 20px">
          <div style={{ fontSize: 'var(--font-size-hero)', fontWeight: 'var(--font-weight-bold)', color: colors.darkText, marginBottom: '4px' }}>{accountsWithIntel}</div>
          <div style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>with intelligence</div>
        </Card>
      </div>

      <SalesAccountDirectory key="sales-directory" token={token} />

      {/* Account Cards */}
      {accounts.length === 0 ? (
        <Card style={{ padding: '60px', textAlign: 'center' }}>
          <div style={{ fontSize: 'var(--font-size-base)', color: colors.textMuted }}>
            No customer accounts have been loaded yet.
          </div>
        </Card>
      ) : (
        <Card padding="0">
          <table className="standard-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Account</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Revenue</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Status</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Suppliers</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Intelligence</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map(acct => (
                <tr
                  key={acct.id}
                  style={{ borderBottom: `1px solid ${colors.mediumGray}`, cursor: 'pointer' }}
                  onClick={() => navigate(`/accounts/${acct.id}`)}
                >
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 'var(--font-weight-semibold)', color: colors.electricBlue }}>{acct.name}</div>
                    {acct.context_type && <div style={{ fontSize: 'var(--font-size-xs)', color: colors.textMuted }}>{acct.context_type}</div>}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'var(--font-weight-medium)', fontFamily: 'monospace' }}>
                    {acct.annual_value ? formatCurrency(acct.annual_value) : 'Not available'}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <Badge variant={acct.status === 'active' ? 'success' : 'secondary'}>{acct.status || 'unknown'}</Badge>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center', color: colors.textMuted }}>
                    {acct.linked_suppliers?.length || 0} linked
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center', color: acct.intelligence_count > 0 ? colors.darkText : colors.textMuted }}>
                    {acct.intelligence_count > 0 ? `${acct.intelligence_count} entries` : 'None yet'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

// ═══════ ACCOUNT DETAIL VIEW ═══════

export function AccountDetail({ accountId, embedded = false }) {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAccountDetail();
  }, [accountId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAccountDetail = async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/accounts/${accountId}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (res.ok) {
        setAccount(await res.json());
      } else if (res.status === 404) {
        setError('Account not found');
      } else {
        setError('Failed to load account details');
      }
    } catch (err) {
      console.error('Error fetching account detail:', err);
      setError('Failed to load account details');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '$0';
    if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not available';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getIntelTypeBadge = (tags) => {
    if (!tags) return { label: 'General', bg: '#F3F4F6', color: '#6B7280' };
    const t = typeof tags === 'string' ? tags.toLowerCase() : Array.isArray(tags) ? tags[0]?.toLowerCase() : '';
    if (t.includes('headwind')) return { label: 'Headwind', bg: '#FEF2F2', color: '#991B1B' };
    if (t.includes('tailwind')) return { label: 'Tailwind', bg: '#ECFDF5', color: '#065F46' };
    if (t.includes('competitive') || t.includes('threat')) return { label: 'Competitive Threat', bg: '#FFF7ED', color: '#9A3412' };
    if (t.includes('growth') || t.includes('opportunity')) return { label: 'Growth Opportunity', bg: '#EFF6FF', color: '#1E40AF' };
    if (t.includes('relationship')) return { label: 'Relationship', bg: '#F5F3FF', color: '#5B21B6' };
    return { label: typeof tags === 'string' ? tags : Array.isArray(tags) ? tags.join(', ') : 'General', bg: '#F3F4F6', color: '#6B7280' };
  };

  if (loading) {
    return (
      <div style={{ padding: '32px', backgroundColor: colors.lightGray, minHeight: '100vh' }}>
        <LoadingSpinner text="Loading account details..." />
      </div>
    );
  }

  if (error || !account) {
    return (
      <div style={{ padding: '32px' }}>
        <Card style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: 'var(--font-size-sm)', color: colors.danger, marginBottom: '16px' }}>{error || 'Account not found'}</div>
          <button onClick={() => navigate('/customers')} style={{ background: 'none', border: 'none', color: colors.electricBlue, cursor: 'pointer', fontSize: 'var(--font-size-base)' }}>
            {'\u2190'} Back to Customers
          </button>
        </Card>
      </div>
    );
  }

  // Collect populated overview fields
  const overviewFields = [];
  if (account.context_type) overviewFields.push({ label: 'Type', value: account.context_type });
  if (account.strategic_tier) overviewFields.push({ label: 'Strategic Tier', value: account.strategic_tier });
  if (account.source) overviewFields.push({ label: 'Source', value: account.source });
  if (account.key_contacts) overviewFields.push({ label: 'Key Contacts', value: account.key_contacts });
  if (account.margin_notes) overviewFields.push({ label: 'Margin Notes', value: account.margin_notes });
  if (account.strategic_notes) overviewFields.push({ label: 'Strategic Notes', value: account.strategic_notes });

  return (
    <div style={{ padding: embedded ? '24px 0 0' : '32px', backgroundColor: colors.lightGray, minHeight: embedded ? undefined : '100vh' }}>
      {!embedded && <>
      {/* Back link */}
      <div style={{ marginBottom: '16px' }}>
        <button onClick={() => navigate('/customers')} style={{ background: 'none', border: 'none', color: colors.electricBlue, cursor: 'pointer', fontSize: 'var(--font-size-base)', padding: 0, display: 'flex', alignItems: 'center', gap: '4px' }}>
          {'\u2190'} Back to Customers
        </button>
      </div>

      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: 'var(--font-size-hero)', fontWeight: 'var(--font-weight-bold)', color: colors.darkText, margin: '0 0 8px 0' }}>
          {account.name}
        </h1>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {account.annual_value > 0 && (
            <span style={{ fontSize: 'var(--font-size-md)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText }}>
              {formatCurrency(account.annual_value)}
            </span>
          )}
          <Badge variant={account.status === 'active' ? 'success' : 'secondary'}>{account.status || 'unknown'}</Badge>
        </div>
      </div>

      </>}
      {!embedded && <SalesAccountDirectory token={token} accountId={accountId} />}
      {/* Section 1: Overview */}
      {overviewFields.length > 0 && (
        <Card style={{ marginBottom: '24px' }}>
          <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 'var(--letter-spacing-wide)', marginBottom: '16px' }}>
            Overview
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {overviewFields.map((field, idx) => (
              <div key={idx}>
                <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>{field.label}</div>
                <div style={{ fontSize: 'var(--font-size-sm)', color: colors.darkText, lineHeight: 1.5 }}>{field.value}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Section 2: Linked Suppliers */}
      <Card style={{ marginBottom: '24px' }}>
        <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 'var(--letter-spacing-wide)', marginBottom: '16px' }}>
          Linked Suppliers ({account.linked_suppliers?.length || 0})
        </div>
        {account.linked_suppliers?.length > 0 ? (
          <table className="standard-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Supplier</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Criticality</th>
              </tr>
            </thead>
            <tbody>
              {account.linked_suppliers.map((s, idx) => (
                <tr key={idx} style={{ borderBottom: `1px solid ${colors.mediumGray}`, cursor: 'pointer' }} onClick={() => navigate(`/suppliers/${encodeURIComponent(s.supplier_name)}`)}>
                  <td style={{ ...tdStyle, fontWeight: 'var(--font-weight-semibold)', color: colors.electricBlue }}>{s.supplier_name}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    {s.criticality ? (
                      <Badge variant={s.criticality === 'critical' ? 'danger' : s.criticality === 'high' ? 'warning' : 'secondary'}>{s.criticality}</Badge>
                    ) : 'Not available'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={{ padding: '20px', textAlign: 'center', color: colors.textMuted, fontSize: 'var(--font-size-sm)' }}>
            No suppliers have been linked to this account yet.
          </div>
        )}
      </Card>

      {/* Section 3: Intelligence */}
      <Card>
        <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 'var(--letter-spacing-wide)', marginBottom: '16px' }}>
          Intelligence ({account.intelligence_entries?.length || 0})
        </div>
        {account.intelligence_entries?.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {account.intelligence_entries.map((entry, idx) => {
              const badge = getIntelTypeBadge(entry.tags);
              return (
                <div key={entry.id || idx} style={{ padding: '12px 16px', backgroundColor: colors.lightGray, borderRadius: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-semibold)', padding: '2px 8px', borderRadius: '4px', backgroundColor: badge.bg, color: badge.color }}>
                      {badge.label}
                    </span>
                    <span style={{ fontSize: 'var(--font-size-xs)', color: colors.textMuted }}>{formatDate(entry.created_at)}</span>
                  </div>
                  <div style={{ fontSize: 'var(--font-size-sm)', color: colors.darkText, lineHeight: '1.5' }}>{entry.content}</div>
                  {entry.source_type && (
                    <div style={{ fontSize: 'var(--font-size-xs)', color: colors.textMuted, marginTop: '6px' }}>
                      Source: {entry.source_type}{entry.contributed_by ? ` by ${entry.contributed_by}` : ''}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ padding: '20px', textAlign: 'center', color: colors.textMuted, fontSize: 'var(--font-size-sm)' }}>
            No intelligence has been captured for this account yet.
          </div>
        )}
      </Card>
    </div>
  );
}

// ===== Table Styles =====
const thStyle = {
  padding: '8px 12px',
  textAlign: 'left',
  fontWeight: 'var(--font-weight-medium)',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  borderBottom: '1px solid #E2E5EA',
  backgroundColor: 'transparent',
  whiteSpace: 'nowrap',
};

const tdStyle = {
  padding: '10px 12px',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-primary)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export default Accounts;
